import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { UPSCALE_IMAGE_MODELS, UPSCALE_VIDEO_MODELS } from '../constants/models';
import * as api from '../services/api';
import Icons from '../components/Icons';
import Lightbox from '../components/Lightbox';
import { groupItemsByDate } from '../utils/grouping';
import toast from 'react-hot-toast';
import { downloadFile } from '../utils/downloadFile';
import MediaLibraryModal from '../components/MediaLibraryModal';
import { confirmAlert } from '../utils/confirmAlert';

export default function UpscalePage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, items, refreshItems, removeItem, pendingJobs, setPendingJobs } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();

  const incoming = location.state || {};
  const [tab, setTab] = useState('image'); // 'image' or 'video'
  const [sourceUrl, setSourceUrl] = useState(incoming.sourceUrl || null);
  const [sourceFile, setSourceFile] = useState(null); // File or Library asset
  const [libOpen, setLibOpen] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  
  // Model state
  const [imageModel, setImageModel] = useState('topaz-image');
  const [videoModel, setVideoModel] = useState('topaz-video');
  const [showModelPicker, setShowModelPicker] = useState(false);

  // Options state
  const [imageRes, setImageRes] = useState('2x');
  const [videoRes, setVideoRes] = useState('2x');
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [fsIdx, setFsIdx] = useState(0);
  const [cd, setCd] = useState(0);
  const [lbItem, setLbItem] = useState(null);

  const srcRef = useRef();
  const cdRef = useRef();
  const genLockRef = useRef(false);

  // Switch models based on tab
  const activeModels = tab === 'image' ? UPSCALE_IMAGE_MODELS : UPSCALE_VIDEO_MODELS;
  const currentModelId = tab === 'image' ? imageModel : videoModel;
  const currentModel = activeModels.find(m => m.id === currentModelId) || activeModels[0];

  const showResolution = !currentModel.hideResolution;
  const activeRes = tab === 'image' ? imageRes : videoRes;

  // Cost calculation
  const getCost = () => {
    let cost = currentModel.cost;
    if (showResolution && currentModel.resPrices && currentModel.resPrices[activeRes]) {
      cost = currentModel.resPrices[activeRes];
    }
    if (tab === 'video') {
      return (Math.ceil(videoDuration) || 1) * cost;
    }
    return cost;
  };
  const cost = getCost();

  const localJobs = pendingJobs.filter(j => j.type === 'upscale');

  const startCD = (s) => {
    setCd(s);
    if (cdRef.current) clearInterval(cdRef.current);
    cdRef.current = setInterval(() => {
      setCd(p => { if (p <= 1) { clearInterval(cdRef.current); return 0; } return p - 1; });
    }, 1000);
  };

  useEffect(() => {
    return () => { if (cdRef.current) clearInterval(cdRef.current); };
  }, []);

  useEffect(() => {
    if (tab === 'video') {
      if (sourceFile) {
        if (sourceFile.fileUrl) {
          // It's a library asset
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.crossOrigin = 'anonymous';
          video.onloadedmetadata = () => {
            setVideoDuration(video.duration || 0);
          };
          video.src = sourceFile.fileUrl;
        } else {
          // Local file
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.onloadedmetadata = () => {
            window.URL.revokeObjectURL(video.src);
            setVideoDuration(video.duration || 0);
          };
          video.src = URL.createObjectURL(sourceFile);
        }
      } else if (sourceUrl) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.crossOrigin = 'anonymous';
        video.onloadedmetadata = () => {
          setVideoDuration(video.duration || 0);
        };
        video.src = sourceUrl;
      } else {
        setVideoDuration(0);
      }
    } else {
      setVideoDuration(0);
    }
  }, [tab, sourceFile, sourceUrl]);

  // Sync state if coming from lightbox while already mounted
  const incomingProcessed = useRef(false);

  useEffect(() => {
    const st = location.state;
    if (st && st.sourceUrl && st.action === 'upscale' && !incomingProcessed.current) {
      incomingProcessed.current = true;
      setTab('image');
      // Set URL immediately for instant preview — no slow proxy download on load
      setSourceUrl(st.sourceUrl);
      setSourceFile(null);
      // clear state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const handleGen = useCallback(async () => {
    if (genLockRef.current) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (cd > 0) { toast.error(`Wait ${cd}s`); return; }
    if (credits < cost) { toast.error(`Need ${cost} credits`); navigate('/subscription'); return; }
    if (!sourceFile && !sourceUrl) { toast.error(`Upload a source ${tab}`); return; }
    genLockRef.current = true;

    // Create pending job placeholders
    const jobId = `upend_${Date.now()}`;
    const newJob = {
      id: jobId, prompt: `Upscaling ${tab}...`, model: currentModel.label, type: 'upscale',
    };
    setPendingJobs(prev => [newJob, ...prev]);
    setLoading(true);
    startCD(8);

    // Fire real API call in background
    (async () => {
      try {
        let fileToSend = sourceFile?.fileUrl || sourceFile;
        if (!fileToSend && sourceUrl) {
          try {
            const resp = await fetch(sourceUrl);
            const blob = await resp.blob();
            fileToSend = new File([blob], `source.${tab === 'image' ? 'png' : 'mp4'}`, { type: blob.type || (tab === 'image' ? 'image/png' : 'video/mp4') });
          } catch {
            toast.error(`Failed to load source ${tab}`);
            setPendingJobs(prev => prev.filter(j => j.id !== jobId));
            setLoading(false);
            return;
          }
        }

        // Convert UI resolution (1x, 2x, 4x, 8x) to API upscale_factor (1, 2, 4, 8)
        const upscaleFactor = activeRes ? activeRes.replace('x', '') : '2';

        let res;
        if (tab === 'video') {
          res = await api.upscaleVideo(token, {
            model: currentModelId,
            sourceVideo: fileToSend,
            sourceUrl: (typeof fileToSend === 'string') ? fileToSend : (!fileToSend ? sourceUrl : undefined),
            upscaleFactor,
            duration: Math.ceil(videoDuration) || 1,
          });
        } else {
          res = await api.upscaleImage(token, {
            model: currentModelId,
            sourceImage: fileToSend,
            sourceUrl: (typeof fileToSend === 'string') ? fileToSend : (!fileToSend ? sourceUrl : undefined),
            upscaleFactor,
          });
        }

        if (res.success) {
          setCredits(res.creditsRemaining);
          setResults([res.imageUrl]);
          toast.success('Upscale complete!');
          refreshItems(true);
        } else {
          toast.error(res.error || 'Upscale failed');
        }
      } catch (e) {
        const msg = e.response?.data?.error;
        const st = e.response?.status;
        if (st === 429) toast.error(msg || 'Rate limited');
        else if (st === 402) toast.error('Not enough credits');
        else toast.error(msg || 'Upscale failed. Credits refunded.');
      } finally {
        setPendingJobs(prev => prev.filter(j => j.id !== jobId));
        genLockRef.current = false;
        setLoading(false);
      }
    })();
  }, [tab, currentModel, currentModelId, sourceFile, sourceUrl, videoDuration, cost, credits, cd, token, user, activeRes, navigate, setCredits, refreshItems, onShowAuth]);

  const toggleFullscreen = (idx = 0) => { setFsIdx(idx); setFullscreen(!fullscreen); };
  const result = results[0] || null;

  const handleDeleteItem = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try {
      await api.deleteGeneration(token, id);
      removeItem(id);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const recentImages = (items || [])
    .filter(i => tab === 'image' ? (i.type === 'image' || i.type === 'upscale' || i.type === 'edit') : i.type === 'video')
    .sort((a, b) => {
      const ta = a.createdAt?.seconds || a.createdAt?._seconds || 0;
      const tb = b.createdAt?.seconds || b.createdAt?._seconds || 0;
      return tb - ta;
    })
    .slice(0, 12);

  const handleTabSwitch = (newTab) => {
    setTab(newTab);
    setSourceFile(null);
    setSourceUrl(null);
    setResults([]);
  };

  const handleModelChange = (id) => {
    if (tab === 'image') {
      setImageModel(id);
      // Reset res
      const m = UPSCALE_IMAGE_MODELS.find(x => x.id === id);
      if (m && m.resolutions && !m.resolutions.includes(imageRes)) {
        setImageRes(m.resolutions[0]);
      }
    } else {
      setVideoModel(id);
      // Reset res
      const m = UPSCALE_VIDEO_MODELS.find(x => x.id === id);
      if (m && m.resolutions && !m.resolutions.includes(videoRes)) {
        setVideoRes(m.resolutions[0]);
      }
    }
    setShowModelPicker(false);
  };

  return (
    <>
      {fullscreen && results[fsIdx] && (
        <div className="img-fs" onClick={() => setFullscreen(false)}>
          {tab === 'image' ? (
            <img src={results[fsIdx]} alt="" className="img-fs-img" onClick={e => e.stopPropagation()} />
          ) : (
            <video src={results[fsIdx]} className="img-fs-img" controls autoPlay loop onClick={e => e.stopPropagation()} />
          )}
          <button className="img-fs-close" onClick={() => setFullscreen(false)}><Icons.X sz={16} /></button>
        </div>
      )}

      {/* Model Picker Popup */}
      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}><Icons.X sz={14} /></button>
            </div>
            <div className="mp-body">
              {activeModels.map(m => (
                <button key={m.id} className={`mp-item ${currentModelId === m.id ? 'on' : ''}`}
                  onClick={() => handleModelChange(m.id)}>
                  {m.icon && Icons[m.icon] ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                      {React.createElement(Icons[m.icon], { sz: 18, color: m.color })}
                    </div>
                  ) : <div className="mp-dot" style={{ background: m.color }} />}
                  <div className="mp-info">
                    <div className="mp-name">{m.label}</div>
                    <div className="mp-cost">{m.cost}+ credits per upscale</div>
                  </div>
                  {currentModelId === m.id && (
                    <div className="mp-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.Upscale sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      <section className="panel">
        <div className="phead"><Icons.Sparkle sz={16} /> Upscale {tab === 'image' ? 'Image' : 'Video'}</div>

        {/* Mode Tabs */}
        <div className="psec">
          <div className="edit-mode-tabs">
            {['image', 'video'].map(m => (
              <button key={m} className={`edit-mode-tab ${tab === m ? 'on' : ''}`} onClick={() => handleTabSwitch(m)}>
                <span className="emt-icon">{m === 'image' ? <Icons.ImgFrame sz={14} /> : <Icons.FilmStrip sz={14} />}</span>
                <span className="emt-label">{m.charAt(0).toUpperCase() + m.slice(1)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Model Selector */}
        <div className="psec">
          <div className="slbl">Model</div>
          <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
            <div className="mp-trigger-left">
              {currentModel.icon && Icons[currentModel.icon] ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 22, height: 22, marginRight: 10, color: currentModel.color }}>
                  {React.createElement(Icons[currentModel.icon], { sz: 20, color: currentModel.color })}
                </div>
              ) : <div className="mp-dot" style={{ background: currentModel.color }} />}
              <div>
                <div className="mp-trigger-name">{currentModel.label}</div>
              </div>
            </div>
            <div className="mp-trigger-right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </button>
        </div>

        {/* Source File */}
        <div className="psec">
          <div className="slbl">Source {tab === 'image' ? 'Image' : 'Video'} <span className="opt">— {sourceUrl ? 'from gallery' : 'upload'}</span></div>
          {sourceUrl && !sourceFile ? (
            <div className="edit-source-preview">
              {tab === 'image' ? (
                <img src={sourceUrl} alt="" className="edit-src-img" />
              ) : (
                <video src={sourceUrl} className="edit-src-img" />
              )}
              <div className="edit-src-actions">
                <button className="edit-src-clear" onClick={() => setSourceUrl(null)}><Icons.X sz={10} /> Remove</button>
                <button className="edit-src-clear" onClick={() => srcRef.current?.click()}><Icons.Upload sz={10} /> Replace</button>
              </div>
            </div>
          ) : (
            <div className={`uzone ${sourceFile ? 'on' : ''}`} onClick={() => setLibOpen(true)}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { 
                e.preventDefault(); 
                if (e.dataTransfer.files[0]) { 
                  setSourceFile(e.dataTransfer.files[0]); 
                  setSourceUrl(null); 
                } 
              }}>
              {sourceFile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                    {tab === 'image' ? (
                      <img src={sourceFile.fileUrl || URL.createObjectURL(sourceFile)} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                    ) : (
                      <video src={sourceFile.fileUrl || URL.createObjectURL(sourceFile)} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                    )}
                    <button onClick={(e) => { e.stopPropagation(); setSourceFile(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                  <span style={{ color: 'var(--t2)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sourceFile.name || 'Library Media'}</span>
                </div>
              ) : (
                <>
                  <div style={{ opacity: .4 }}><Icons.Upload /></div>
                  <div style={{ fontSize: 12 }}>Upload source {tab}</div>
                </>
              )}
            </div>
          )}
          <input ref={srcRef} type="file" accept={tab === 'image' ? "image/*" : "video/*"} style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) { setSourceFile(e.target.files[0]); setSourceUrl(null); } }} />
        </div>

        {/* Resolution Options */}
        {showResolution && currentModel.resolutions && (
          <div className="psec">
            <div className="slbl">Upscale Factor</div>
            <div className="pills" style={{ marginBottom: 14 }}>
              {currentModel.resolutions.map(r => (
                <button key={r} className={`pill ${activeRes === r ? 'on' : ''}`}
                  onClick={() => tab === 'image' ? setImageRes(r) : setVideoRes(r)}>
                  {r.toUpperCase()}
                  {currentModel.resPrices && <span style={{ fontSize: 9, color: 'var(--or)', marginLeft: 4 }}>{currentModel.resPrices[r]}{tab === 'video' ? 'cr/s' : 'cr'}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Generate Button */}
        <div className="gen-sticky">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', textAlign: 'center' }}>
            <strong style={{ color: currentModel.color }}>{currentModel.label}</strong>
            {showResolution && activeRes && <span>· {activeRes.toUpperCase()}</span>}
          </div>
          <div className="cost-row">
            <span className="cost-l">Cost</span>
            <span className="cost-v">
              {cost} cr {' → '}{Math.max(0, (credits || 0) - cost)} left
            </span>
          </div>
          <button className="gbtn" onClick={handleGen}
            disabled={loading || (user && credits < cost) || cd > 0}>
            {cd > 0 ? `Wait ${cd}s` :
              !user ? <>✨ Sign up to Upscale</> :
              <><Icons.Sparkle sz={15} /> Upscale</>}
          </button>
          {user && credits < cost && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }}
              onClick={() => navigate('/subscription')}>Insufficient credits — <u>upgrade</u></p>
          )}
        </div>
      </section>

      {/* Main Canvas */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on"><Icons.Upscale sz={13} /> Preview</div>
        </div>
        <div className="mbody">
          {results.length > 0 ? (
            <div className="prev">
              <div className="prev-wrap">
                <div className="prev-toolbar">
                    <div className="prev-tb-right" style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
<button className="prev-icon-btn" onClick={() => toggleFullscreen(0)} title="Fullscreen">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
                    </button>
<button className="prev-icon-btn" onClick={() => downloadFile(result, token, tab !== 'image').catch(() => toast.error('Download failed'))} title="Download"><Icons.DL sz={15} /></button>
                    <button className="prev-icon-btn del" onClick={async () => {
                      const isConfirmed = await confirmAlert({
                        title: 'Clear Results?',
                        message: 'Are you sure you want to clear these results?'
                      });
                      if (isConfirmed) setResults([]);
                    }} title="Remove"><Icons.Trash sz={15} /></button>
                    </div>
                  </div>

                {tab === 'image' ? (
                  <img src={results[0]} alt="" />
                ) : (
                  <video src={results[0]} controls autoPlay loop style={{ width: '100%', borderRadius: 8 }} />
                )}
              </div>
            </div>
          ) : (localJobs.length > 0 || recentImages.length > 0) ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Grid sz={13} /> {localJobs.length > 0 ? 'Loading...' : `Recent ${tab === 'image' ? 'Images' : 'Videos'}`}
              </div>
              <div className="ggrid">
                {localJobs.map(j => (
                  <div key={j.id} className="gitem-pending">
                    <div className="pend-icon">
                      {tab === 'image' ? '🖼' : (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                          <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
                          <line x1="7" y1="2" x2="7" y2="22"></line>
                          <line x1="17" y1="2" x2="17" y2="22"></line>
                          <line x1="2" y1="12" x2="22" y2="12"></line>
                          <line x1="2" y1="7" x2="7" y2="7"></line>
                          <line x1="2" y1="17" x2="7" y2="17"></line>
                          <line x1="17" y1="17" x2="22" y2="17"></line>
                          <line x1="17" y1="7" x2="22" y2="7"></line>
                        </svg>
                      )}
                    </div>
                    <div className="pend-label">{j.prompt || 'Upscaling...'}</div>
                    <div className="pend-model">{j.model}</div>
                    <div className="pend-bar-wrap"><div className="pend-bar" /></div>
                    <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 10, textAlign: 'center' }}>Estimated time 4 to 10 min</div>
                  </div>
                ))}
              </div>
              
              <div className="grouped-collections">
                {groupItemsByDate(recentImages).map(group => (
                  <div key={group.label} className="date-group-wrap">
                    <div className="date-group-label" style={{ marginBottom: 12 }}>{group.label}</div>
                    <div className="ggrid">
                      {group.items.map(i => (
                        <div key={i.id} className="gitem" onClick={() => setLbItem(i)}>
                          {tab === 'image' || i.type === 'image' || i.type === 'upscale' ? (
                            <img src={i.imageUrl} alt="" />
                          ) : (
                            <video src={i.videoUrl || i.imageUrl} />
                          )}
                          <div className="gbadge" style={{ background: 'rgba(139,92,246,.12)', color: '#8b5cf6' }}>{tab === 'image' ? <Icons.ImgFrame sz={11} /> : <Icons.FilmStrip sz={11} />}</div>
                          <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); if(typeof handleDeleteItem === 'function') handleDeleteItem(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                            <div className="gm">
                              <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{i.prompt || 'Upscaled'}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{i.modelName || i.model}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--t3)', fontSize: 13, flexDirection: 'column', gap: 12 }}>
              <Icons.Sparkle sz={32} />
              <p>Upload a source and click Upscale</p>
            </div>
          )}
        </div>
      </div>
      
      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDeleteItem} />

      <MediaLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        allowedTypes={tab === 'image' ? ['image'] : ['video']}
        maxSelect={1}
        maxTypeCounts={{ image: 1, video: 1, audio: 0 }}
        onSelect={(assets) => {
          setSourceFile(assets[0]);
          setSourceUrl(null);
        }}
      />
    </>
  );
}


